/**
 * TrackingCodes - Framework-agnostic manager for Meta Pixel and Google Tag Manager with cookie consent
 * 
 * @example
 * // Initialize in any JavaScript application:
 * import { createTrackingManager } from '@/Shared/Composables/UseTrackingCodes';
 * 
 * const trackingManager = createTrackingManager({
 *   metaPixelId: 'YOUR_PIXEL_ID',
 *   googleTagManagerId: 'GTM-XXXXXX',
 *   showBanner: () => {
 *     // Your custom banner display logic
 *     document.getElementById('cookieBanner').style.display = 'block';
 *   }
 * });
 * 
 * // Initialize tracking (checks consent and loads scripts or shows banner)
 * trackingManager.initialize();
 * 
 * // Hook up your banner buttons:
 * document.getElementById('acceptBtn').onclick = trackingManager.acceptCookies;
 * document.getElementById('denyBtn').onclick = trackingManager.denyCookies;
 * 
 * // Track custom events after consent:
 * trackingManager.trackEvent('Purchase', { value: 99.99, currency: 'USD' });
 * 
 * // Check current state:
 * console.log(trackingManager.getConsentStatus()); // true, false, or null
 * console.log(trackingManager.areScriptsLoaded()); // boolean
 */

const CONSENT_KEY = 'cookie_consent';

export function createTrackingManager(config = {}) {
  const { metaPixelId, googleTagManagerId, showBanner } = config;
  
  let consentGiven = null;
  let scriptsLoaded = false;

  /**
   * Check if user has previously given consent
   */
  const checkConsent = () => {
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (stored !== null) {
        consentGiven = stored === 'true';
        return consentGiven;
      }
      return null;
    } catch (error) {
      console.error('Error checking consent:', error);
      return null;
    }
  };

  /**
   * Save consent status to localStorage
   */
  const saveConsent = (accepted) => {
    try {
      localStorage.setItem(CONSENT_KEY, accepted.toString());
      consentGiven = accepted;
    } catch (error) {
      console.error('Error saving consent:', error);
    }
  };

  /**
   * Load Meta Pixel tracking script
   */
  const loadMetaPixel = () => {
    if (!metaPixelId || window.fbq) {
      return;
    }

    // Meta Pixel Base Code
    !function(f,b,e,v,n,t,s) {
      if(f.fbq) return;
      n=f.fbq=function(){
        n.callMethod ?
        n.callMethod.apply(n,arguments) : n.queue.push(arguments)
      };
      if(!f._fbq) f._fbq=n;
      n.push=n;
      n.loaded=!0;
      n.version='2.0';
      n.queue=[];
      t=b.createElement(e);
      t.async=!0;
      t.src=v;
      s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)
    }(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');

    window.fbq('init', metaPixelId);
    window.fbq('track', 'PageView');

    console.log('Meta Pixel loaded:', metaPixelId);
  };


  /**
   * Load Google Tag Manager (GTM-XXXX) or GA4 Tag (G-XXXX)
   */
  const loadGoogleTracking = () => {
    if (!googleTagManagerId || window.dataLayer) {
      return;
    }
    if (window.__googleTrackingLoaded) return;
    window.__googleTrackingLoaded = true;

    // --- Case 1: ID starts with "GTM-" â†’ load Google Tag Manager ---
    if (googleTagManagerId.startsWith("GTM-")) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
      });

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${googleTagManagerId}`;
      document.head.appendChild(script);

      console.log("Loaded GTM:", googleTagManagerId);
      return;
    }

    // --- Case 2: ID starts with "G-" â†’ load GA4 global site tag ---
    if (googleTagManagerId.startsWith("G-")) {
      // Load GA4 "gtag.js"
      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${googleTagManagerId}`;
      document.head.appendChild(script);

      // Initialise GA4
      window.dataLayer = window.dataLayer || [];
      function gtag(){ window.dataLayer.push(arguments); }
      window.gtag = gtag;

      gtag("js", new Date());
      gtag("config", googleTagManagerId);

      console.log("Loaded GA4 gtag:", googleTagManagerId);
      return;
    }

    console.warn("Unknown Google ID format:", googleTagManagerId);
  };


  /**
   * Load all tracking scripts
   */
  const loadTrackingScripts = () => {
    if (scriptsLoaded) {
      return;
    }

    if (metaPixelId) {
      loadMetaPixel();
    }

    if (googleTagManagerId) {
      loadGoogleTracking();
    }

    scriptsLoaded = true;
  };

  /**
   * Handle cookie acceptance
   */
  const acceptCookies = () => {
    saveConsent(true);
    loadTrackingScripts();
  };

  /**
   * Handle cookie denial
   */
  const denyCookies = () => {
    saveConsent(false);
    // Optionally clear any tracking data
    if (window.fbq) {
      // Revoke consent for Meta Pixel if needed
      window.fbq('consent', 'revoke');
    }
  };

  /**
   * Initialize tracking based on consent status
   */
  const initialize = () => {

    if(!metaPixelId && !googleTagManagerId) {
      return;
    }

    const consent = checkConsent();

    if (consent === null) {
      // No consent decision made yet, show banner
      if (typeof showBanner === 'function') {
        showBanner();
      } else {
        console.warn('No showBanner function provided');
      }
    } else if (consent === true) {
      // User has previously accepted, load scripts
      loadTrackingScripts();
    }
    // If consent is false, do nothing
  };

  /**
   * Clear consent and reset tracking
   */
  const clearConsent = () => {
    try {
      localStorage.removeItem(CONSENT_KEY);
      consentGiven = null;
      scriptsLoaded = false;
    } catch (error) {
      console.error('Error clearing consent:', error);
    }
  };

  /**
   * Track with GTM
   */
  const pushToGTM = (eventName, eventData) => {
    if (googleTagManagerId && window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...eventData
      });
    }
  };

  /**
     * Track with Meta Pixel
     */
  const pushToMeta = (eventName, eventData) => {
    if (metaPixelId && window.fbq) {
      window.fbq('track', eventName, eventData);
    }
  };


  const pushTrackEvent = (action) => {
    if (!consentGiven) {
      console.warn('Cannot track event: consent not given');
      return;
    }
    action();
  };

  /**
   * Track custom event
   */
  const trackEvent = (eventName, eventData = {}) => {
    const action = () => {
      pushToGTM(eventName, eventData);
      pushToMeta(eventName, eventData);
    };
    pushTrackEvent(action);
  };

  /**
   * SPA route change: GTM dataLayer event + Meta PageView.
   * Skips when consent/scripts are not active (no console noise).
   * In GTM, add a trigger on event name "virtualPageView" and fire GA4 (or map page_path / page_title).
   */
  const trackVirtualPageView = ({ path, title, pageLocation } = {}) => {
    if (!consentGiven || !scriptsLoaded) {
      return;
    }
    const loc =
      pageLocation ??
      (typeof window !== 'undefined' ? window.location.href : '');
    pushToGTM('virtualPageView', {
      page_path: path,
      page_title: title,
      page_location: loc,
    });
    if (metaPixelId && window.fbq) {
      window.fbq('track', 'PageView');
    }
  };

  /**
   * Track purchase
   * Event name is "Purchase" for Meta Pixel and "purchase" for GTM, with appropriate data structure for each
   * Required data for GTM: { value, currency, transaction_id, items } while items is an array of required data { item_id, item_name }
   * Required data for Meta Pixel: { value, currency }
   */
  const trackPurchase = (data) => {
    const { currency, value, paymentId, tickets } = data;

    const action = () => {
      pushToGTM('purchase', {
        currency: currency,
        value: value,
        items: tickets,
        transaction_id: paymentId
      });

      pushToMeta('Purchase', {
        contents: tickets, 
        content_type: 'ticket', 
        currency: currency,
        value: value,
        content_ids: tickets ? tickets.map(i => i.item_id) : []
      });
    };

    pushTrackEvent(action);
  };

  /**
   * Track AddToCart
   * Event name is "AddToCart" for Meta Pixel and "add_to_cart" for GTM, with appropriate data structure for each
   * Required data for GTM: { value, currency, items } while items is an array of required data { item_id, item_name }
   * Required data for Meta Pixel: { value, currency }
   */
  const trackAddToCart = (data) => {
    const { currency, value, profile } = data;

    const items = Object.entries(profile).map(([key, value]) => ({
      item_id: key,
      item_name: value
    }));

    const action = () => {
      pushToGTM('add_to_cart', {
        currency: currency,
        value: value,
        items: items
      });

      pushToMeta('AddToCart', {
        contents: items, 
        content_type: 'profileDetails', 
        currency: currency,
        value: value,
        content_ids: items.map(i => i.item_id)
      });
    }

    pushTrackEvent(action);    
  };

  /**
   * Track Initiate Checkout
   * Event name is "InitiateCheckout" for Meta Pixel and "begin_checkout" for GTM, with appropriate data structure for each
   * Required data for GTM: { value, currency, items } while items is an array of required data { item_id, item_name }
   */
  const trackInitiateCheckout = (data) => {
    const { currency, value, tickets } = data;

    const action = () => {
      pushToGTM('begin_checkout', {
        currency: currency,
        value: value,
        items: tickets
      });

      pushToMeta('InitiateCheckout', {
        contents: tickets, 
        content_type: 'ticket', 
        currency: currency,
        value: value,
        content_ids: tickets ? tickets.map(i => i.item_id) : []
      });
    }

    pushTrackEvent(action);
  };

  /**
   * Track Initiate Checkout
   * Event name is "AddPaymentInfo" for Meta Pixel and "add_payment_info" for GTM, with appropriate data structure for each
   * Required data for GTM: { value, currency, items } while items is an array of required data { item_id, item_name }
   */
  const trackAddPaymentInfo = (data) => {
    const { currency, value, tickets, discountCode, paymentType } = data;

    const action = () => {
      pushToGTM('add_payment_info', {
        currency: currency,
        value: value,
        items: tickets,
        coupon: discountCode,
        payment_type: paymentType
      });

      pushToMeta('AddPaymentInfo', {
        contents: tickets, 
        currency: currency,
        value: value,
        content_ids: tickets ? tickets.map(i => i.item_id) : []
      });
    }

    pushTrackEvent(action);
  };

  const StandardTrackEvents = {
    Purchase: (data) => trackPurchase(data),
    AddToCart: (data) => trackAddToCart(data),
    InitiateCheckout: (data) => trackInitiateCheckout(data),
    AddPaymentInfo: (data) => trackAddPaymentInfo(data),
  };

  /**
   * Get current consent status
   */
  const getConsentStatus = () => {
    return consentGiven;
  };

  /**
   * Check if tracking scripts are loaded
   */
  const areScriptsLoaded = () => {
    return scriptsLoaded;
  };

  return {
    // State getters
    getConsentStatus,
    areScriptsLoaded,
    
    // Actions
    initialize,
    acceptCookies,
    denyCookies,
    clearConsent,
    trackEvent,
    trackVirtualPageView,

    StandardTrackEvents
  };
}